import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { sanitizeBlogHtml } from "@/lib/sanitizeHtml";

const LOCALE_MAP: Record<string, string> = { en: 'en-US', sv: 'sv-SE', ja: 'ja-JP', zh: 'zh-CN' };

const BlogPost = () => {
  const { t, i18n } = useTranslation('blog');
  const { slug } = useParams<{ slug: string }>();
  const dateLocale = LOCALE_MAP[i18n.language] || 'en-US';

  const { data: post, isLoading } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: categories } = useQuery({
    queryKey: ["blog-post-categories", post?.id],
    enabled: !!post?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_post_categories")
        .select("blog_categories(name, slug)")
        .eq("post_id", post!.id);
      if (error) throw error;
      return data?.map((d: any) => d.blog_categories) || [];
    },
  });

  const { data: tags } = useQuery({
    queryKey: ["blog-post-tags", post?.id],
    enabled: !!post?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_post_tags")
        .select("blog_tags(name, slug)")
        .eq("post_id", post!.id);
      if (error) throw error;
      return data?.map((d: any) => d.blog_tags) || [];
    },
  });

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(dateLocale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <main className="min-h-screen pt-24 pb-16">
          <div className="container mx-auto px-6 max-w-3xl">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-card rounded w-3/4" />
              <div className="h-4 bg-card rounded w-1/4" />
              <div className="h-96 bg-card rounded" />
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isScheduledInFuture =
    post && post.published_at && new Date(post.published_at) > new Date();

  if (!post || isScheduledInFuture) {
    return (
      <>
        <Header />
        <main className="min-h-screen pt-24 pb-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">{t('post.notFound')}</h1>
            <Link to="/news" className="text-primary hover:underline">
              {t('post.backToBlog')}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | Insight | Cetasol</title>
        <meta name="description" content={post.excerpt || `Read: ${post.title} | insights from Cetasol on maritime performance and sustainability.`} />
        <link rel="canonical" href={`https://cetasol.com/${post.slug}`} />
        <meta property="og:title" content={`${post.title} | Cetasol`} />
        <meta property="og:description" content={post.excerpt || post.title} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://cetasol.com/${post.slug}`} />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${post.title} | Cetasol`} />
        <meta name="twitter:description" content={post.excerpt || post.title} />
        {post.featured_image && <meta name="twitter:image" content={post.featured_image} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "Insight", "item": "https://cetasol.com/news" },
            { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://cetasol.com/${post.slug}` }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt || post.title,
          "image": post.featured_image || undefined,
          "datePublished": post.published_at || post.created_at,
          "publisher": { "@type": "Organization", "name": "Cetasol", "url": "https://cetasol.com" },
          "mainEntityOfPage": `https://cetasol.com/${post.slug}`
        })}</script>
      </Helmet>
      <Header />
      <main className="min-h-screen pt-24 pb-16">
        <article className="container mx-auto px-6 max-w-3xl">
          {(() => {
            const isPressRelease = categories?.some((cat: any) => cat.slug === "pressrelease");
            const isNews = categories?.some((cat: any) => cat.slug === "news");
            const isWhitePaper = categories?.some((cat: any) => cat.slug === "whitepaper");
            const backTo = isWhitePaper ? "/whitepapers" : isPressRelease ? "/insight/press-release" : isNews ? "/news" : "/insight/blog-post";
            const backLabel = isWhitePaper ? t('whitepapers.backTo') : isPressRelease ? t('post.backToPressReleases') : isNews ? t('post.backToNews') : t('post.backToBlogPosts');
            return (
              <Link
                to={backTo}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
              >
                <ArrowLeft size={16} />
                {backLabel}
              </Link>
            );
          })()}

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{post.title}</h1>
            <p className="text-muted-foreground text-sm">
              {formatDate(post.published_at || post.created_at)}
            </p>
            {categories && categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {categories.map((cat: any) => (
                  <Badge key={cat.slug} variant="secondary">
                    {cat.name}
                  </Badge>
                ))}
              </div>
            )}
            {post.pdf_url && (
              <a href={post.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-4">
                <Button className="gap-2">
                  <Download size={16} />
                  {t('post.downloadPdf')}
                </Button>
              </a>
            )}
          </header>

          {post.featured_image && (
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full rounded-lg mb-8"
            />
          )}

          <div
            className="prose prose-invert prose-lg max-w-none
              prose-headings:text-foreground
              prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-12 prose-h2:mb-4 prose-h2:border-b prose-h2:border-border prose-h2:pb-3
              prose-h3:text-xl prose-h3:font-semibold prose-h3:mt-10 prose-h3:mb-3
              prose-h4:text-lg prose-h4:font-semibold prose-h4:mt-8 prose-h4:mb-3
              prose-p:text-foreground/80 prose-p:leading-relaxed prose-p:mb-4
              prose-a:text-primary prose-a:underline prose-a:underline-offset-4
              prose-strong:text-foreground prose-strong:font-semibold
              prose-ul:my-4 prose-ul:space-y-1.5 prose-ol:my-4 prose-ol:space-y-1.5
              prose-li:text-foreground/80 prose-li:leading-relaxed
              prose-blockquote:border-l-4 prose-blockquote:border-primary/40 prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-foreground/70 prose-blockquote:my-6
              prose-img:rounded-lg prose-img:mx-auto prose-img:my-8
              prose-figure:my-8
              prose-hr:border-border prose-hr:my-10
              "
            dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(post.content || "") }}
          />

          {post.pdf_url && (
            <div className="flex justify-center mt-10">
              <a href={post.pdf_url} target="_blank" rel="noopener noreferrer">
                <Button className="gap-2">
                  <Download size={16} />
                  {t('post.downloadPdf')}
                </Button>
              </a>
            </div>
          )}


          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-border">
              {tags.map((tag: any) => (
                <Badge key={tag.slug} variant="outline">
                  #{tag.name}
                </Badge>
              ))}
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
};

export default BlogPost;
